import {
	InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory,
	InvestmentBulkEnhancementConfigurationControllerV4ApiFactory,
	StepAvailabilityStatus,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { type ContextContent, parallelize, useHashState } from "$root/utils";
import { useQueryReactor, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { type SetterOrUpdater, maybeCall, switchExpr } from "@mdotm/shadcn/legacy/utils";
import { createContext, useCallback, useMemo, useRef, useState } from "react";
import { type MultiStepFormV2Props, type StepMetadata, MultiStepFormV2 } from "../../MultiStepFormV2/MultiStepFormV2";
import { Step06_MarketView, getBulkMarketViewStepData } from "./Steps/06-MarketView";
import { makeStepToRequestMapper, responseToStepMapper } from "./requests-multi";
import { makeUnsavedDataHandler } from "./shared";
import {
	defaultEditPortfolioMultiV4StepData,
	editPortfolioMultiV4InitialStepsMetadataByMode,
	editPortfolioMultiV4OrderedSteps,
	type EditMulti_MultiStepPortfolioContext,
	type EditMulti_SubmittersRef,
} from "./shared-multi";
import type { EditPortfolioMultiV4Props } from "./EditPortfolioMulti.shared-types";

const TypedMultiStepper = (
	props: MultiStepFormV2Props<typeof defaultEditPortfolioMultiV4StepData, EditMulti_MultiStepPortfolioContext>,
) => (
	<MultiStepFormV2
		unsavedDataHandler={makeUnsavedDataHandler({
			currentStep: props.currentStep,
			setStepsMetadata: props.setStepsMetadata,
			submittersRef: props.context!.submittersRef,
		})}
		{...props}
	/>
);

const EditPortfolioMultiV4Context = createContext<
	| null
	| ({ canSubmit: boolean } & Omit<EditPortfolioMultiV4Props, "stepsData"> & {
				stepsData: EditPortfolioMultiV4Props["stepsData"];
			})
>(null);

export type EditPortfolioMultiV4ContextType = NonNullable<ContextContent<typeof EditPortfolioMultiV4Context>>;

export function EditPortfolioMultiV4ContextProvider({
	area: propsArea,
	children,
}: {
	area: EditPortfolioMultiV4Props["area"];
	children: NodeOrFn<EditPortfolioMultiV4ContextType>;
}): React.JSX.Element {
	const area = useMemo(
		() => ({
			...propsArea,
			editable: true,
		}),
		[propsArea],
	);

	const defaultStepsMetadata = editPortfolioMultiV4InitialStepsMetadataByMode[area.name];
	const [editedStepsMetadata, _setEditedStepsMetadata] = useState<null | EditPortfolioMultiV4Props["stepsMetadata"]>(
		null,
	);
	const defaultStepsMetadataRef = useUnsafeUpdatedRef(defaultStepsMetadata);
	const setEditedStepsMetadata = useCallback<SetterOrUpdater<EditPortfolioMultiV4Props["stepsMetadata"]>>(
		(x) => _setEditedStepsMetadata((prev) => maybeCall(x, prev ?? defaultStepsMetadataRef.current)),
		[defaultStepsMetadataRef],
	);

	const stepsMetadata = editedStepsMetadata ?? defaultStepsMetadata;
	const [nullableCurrentStep, setCurrentStep] = useHashState({
		allowedValues: editPortfolioMultiV4OrderedSteps,
	});
	const currentStep = nullableCurrentStep ?? "marketView";
	const [stepsData, setStepsData] = useState<EditPortfolioMultiV4Props["stepsData"]>(
		defaultEditPortfolioMultiV4StepData,
	);

	const bulkEnhancementApi = useApiGen(InvestmentBulkEnhancementConfigurationControllerV4ApiFactory);
	const investmentBulkBulkStaticV1Api = useApiGen(InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory);

	const stepsAvailabilityQuery = useQueryNoRefetch({
		queryKey: ["loadProposalSteps", area.proposalUuid, area.bulkUid],
		enabled: area.proposalUuid !== undefined || area.bulkUid !== undefined,
		queryFn: async () => {
			const stepData = await parallelize(
				{
					marketView: () => getBulkMarketViewStepData(bulkEnhancementApi, investmentBulkBulkStaticV1Api, area),
				},
				{ concurrency: 2 },
			);

			const { availabilityMap } = await axiosExtract(
				area.name === "create-bulk-proposal"
					? bulkEnhancementApi.getEnhancementConfigurationStepsAvailability1(
							(area as typeof area & { proposalUuid: string }).proposalUuid,
						)
					: investmentBulkBulkStaticV1Api.getEnhancementConfigurationStepsAvailability2(area.bulkUid),
			);

			return {
				stepData,
				availabilityMap,
			};
		},
	});
	useQueryReactor(stepsAvailabilityQuery, {
		onSuccess({ availabilityMap, stepData }) {
			setStepsData(stepData);
			setEditedStepsMetadata(mergeStepsMetadata(defaultStepsMetadata, availabilityMap));
		},
	});

	const mergeStepMetadata = useCallback(
		(metadata: StepMetadata, availabilityStatus: StepAvailabilityStatus) => ({
			...metadata,
			dirty: false, // if we get here, it means that the user successfully saved the step
			completed: availabilityStatus === StepAvailabilityStatus.Ready, // !empty
			hasErrors: availabilityStatus === StepAvailabilityStatus.ReviewRequired,
			processing: availabilityStatus === StepAvailabilityStatus.Calculating,
		}),
		[],
	);

	const mergeStepsMetadata = useCallback(
		(
			curStepsMetadata: EditPortfolioMultiV4Props["stepsMetadata"],
			availabilityStatusMap?: Partial<Record<"MARKET_VIEW" | "STRATEGY_CONSTRAINTS", StepAvailabilityStatus>>,
		) => {
			if (!availabilityStatusMap) {
				return curStepsMetadata;
			}
			const next = { ...curStepsMetadata };

			next.marketView = !availabilityStatusMap.MARKET_VIEW
				? next.marketView
				: mergeStepMetadata(next.marketView, availabilityStatusMap.MARKET_VIEW);

			return next;
		},
		[mergeStepMetadata],
	);

	const mapper = useMemo(() => makeStepToRequestMapper(area.proposalUuid), [area.proposalUuid]);

	const saveHandlers = useMemo<EditPortfolioMultiV4ContextType["saveHandlers"]>(
		() =>
			({
				// portfolioStrategy: async (values) => {
				// 	const data = await axiosExtract(
				// 		bulkEnhancementApi.setEnhancementConfigurationStrategyConstraints1(mapper.portfolioStrategy(values)),
				// 	);
				// 	setEditedStepsMetadata(mergeStepsMetadata(stepsMetadata, data.stepsAvailability));
				// 	return responseToStepMapper.portfolioStrategy(data, area);
				// },
				marketView: async (values) => {
					if (area.name === "edit-bulk-settings") {
						const data = await axiosExtract(
							investmentBulkBulkStaticV1Api.setStaticConfigurationMarketView1({
								...mapper.marketView(values),
								bulkUUID: area.bulkUid,
							}),
						);
						setEditedStepsMetadata(mergeStepsMetadata(stepsMetadata, data.stepsAvailability));
						return responseToStepMapper.marketView(data, area);
					}

					const data = await axiosExtract(
						bulkEnhancementApi.setEnhancementConfigurationMarketView1(mapper.marketView(values)),
					);
					setEditedStepsMetadata(mergeStepsMetadata(stepsMetadata, data.stepsAvailability));
					return responseToStepMapper.marketView(data, area);
				},
			}) satisfies EditPortfolioMultiV4ContextType["saveHandlers"] as EditPortfolioMultiV4ContextType["saveHandlers"],
		[
			area,
			bulkEnhancementApi,
			investmentBulkBulkStaticV1Api,
			mapper,
			mergeStepsMetadata,
			setEditedStepsMetadata,
			stepsMetadata,
		],
	);

	const canSubmit = useMemo(() => {
		const metadata = Object.values(stepsMetadata);
		// Disable if
		// - there is a non-optional step that hasn't been completed
		// - there is a step containing errors
		// - there is a step asynchronously validating on the server
		// - there is a step marked as dirty
		return !metadata.some(
			(m) => (!m.optional && !m.completed) || m.hasErrors || m.processing || m.dirty || m.persisting,
		);
	}, [stepsMetadata]);

	const submittersRef: EditMulti_SubmittersRef = useRef({});

	const contextValue = useMemo<EditPortfolioMultiV4ContextType>(
		() => ({
			saveHandlers,
			submittersRef,
			currentStep,
			setCurrentStep,
			setStepsMetadata: setEditedStepsMetadata,
			stepsMetadata: editedStepsMetadata ?? defaultStepsMetadata,
			canSubmit,
			setStepsData,
			area,
			stepsData,
		}),
		[
			saveHandlers,
			currentStep,
			setEditedStepsMetadata,
			editedStepsMetadata,
			defaultStepsMetadata,
			canSubmit,
			setCurrentStep,
			area,
			stepsData,
		],
	);

	return (
		<EditPortfolioMultiV4Context.Provider value={contextValue}>
			{renderNodeOrFn(children, contextValue)}
		</EditPortfolioMultiV4Context.Provider>
	);
}

export function EditPortfolioMultiV4InContext(): React.JSX.Element {
	return (
		<EditPortfolioMultiV4Context.Consumer>
			{(data) => <>{data && <EditPortfolioMultiV4 {...data} />}</>}
		</EditPortfolioMultiV4Context.Consumer>
	);
}

export function EditPortfolioMultiV4({
	area,
	currentStep,
	setCurrentStep,
	setStepsData,
	setStepsMetadata,
	stepsData,
	stepsMetadata,
	saveHandlers,
	submittersRef,
}: EditPortfolioMultiV4Props): React.JSX.Element {
	return (
		<TypedMultiStepper
			context={{ area, saveHandlers, submittersRef }}
			currentStep={currentStep}
			setCurrentStep={setCurrentStep}
			stepsMetadata={stepsMetadata}
			setStepsMetadata={setStepsMetadata}
			stepsData={stepsData}
			setStepsData={setStepsData}
			mode={switchExpr(area.name, {
				"create-bulk-proposal": () => "new" as const,
				"edit-bulk-settings": () => "edit" as const,
			})}
			orderedStepNames={editPortfolioMultiV4OrderedSteps}
			componentsByStep={{
				marketView: Step06_MarketView,
			}}
		/>
	);
}
